import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Mail, Phone, Building2, Check, Inbox, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const STATUS_STYLES = {
  new: "bg-gold/15 text-gold border-gold/30",
  read: "bg-white/5 text-ivory/50 border-white/15",
};

export default function AdminInbox() {
  const { user, logout, authAxios } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await authAxios().get("/contact");
      setItems(data);
    } catch {
      toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (user && user !== true) load(); /* eslint-disable-next-line */ }, [user]);

  const markRead = async (id) => {
    try {
      const { data } = await authAxios().patch(`/contact/${id}`, { status: "read" });
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
    } catch { toast.error("Update failed."); }
  };

  const remove = async (id) => {
    try {
      await authAxios().delete(`/contact/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Enquiry deleted.");
    } catch { toast.error("Delete failed."); }
  };

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const matchQ = `${i.name} ${i.email} ${i.company || ""} ${i.category} ${i.message}`.toLowerCase().includes(query.toLowerCase());
      const matchF = filter === "all" || i.status === filter;
      return matchQ && matchF;
    });
  }, [items, query, filter]);

  const newCount = items.filter((i) => i.status === "new").length;

  if (user === null) return <div className="min-h-screen bg-navy grid place-items-center text-ivory/50">Loading…</div>;

  return (
    <div className="min-h-screen bg-navy text-ivory noise-overlay">
      <header className="border-b border-white/10 bg-navy/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <span className="font-serif text-2xl text-ivory">KRV</span>
            <span className="font-serif text-2xl text-gold">Nexus</span>
            <span className="ml-3 text-xs uppercase tracking-[0.2em] text-ivory/40 border-l border-white/15 pl-3">Admin</span>
          </Link>
          <div className="flex items-center gap-5">
            <span className="text-sm text-ivory/50 hidden sm:inline">{user?.email}</span>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-sm text-ivory/70 hover:text-gold transition-colors">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-2">
          <Inbox className="text-gold" size={24} />
          <h1 className="font-serif font-light text-4xl">Enquiry Inbox</h1>
        </div>
        <p className="text-ivory/50" data-testid="inbox-summary">{items.length} total · {newCount} new</p>

        <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
          <div className="relative w-full sm:max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ivory/40" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} data-testid="inbox-search" placeholder="Search enquiries…"
              className="w-full bg-midnight/60 border border-white/10 pl-9 pr-4 py-2.5 text-sm text-ivory focus:outline-none focus:border-gold/50" />
          </div>
          <div className="flex gap-2">
            {["all", "new", "read"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f}`}
                className={`px-4 py-2 text-sm border transition-colors capitalize ${filter === f ? "border-gold text-gold" : "border-white/10 text-ivory/50 hover:text-ivory"}`}>
                {f}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 space-y-4" data-testid="enquiry-list">
          {loading && <p className="text-ivory/40">Loading enquiries…</p>}
          {!loading && filtered.length === 0 && <p className="text-ivory/40" data-testid="empty-state">No enquiries found.</p>}
          {filtered.map((i) => (
            <div key={i.id} data-testid={`enquiry-${i.id}`} className="border border-white/10 bg-midnight/50 p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-serif text-2xl text-ivory">{i.name}</h3>
                    <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-1 border ${STATUS_STYLES[i.status] || STATUS_STYLES.read}`}>{i.status}</span>
                  </div>
                  <p className="text-gold text-xs uppercase tracking-[0.15em] mt-1">{i.category}</p>
                </div>
                <div className="flex items-center gap-3">
                  {i.status !== "read" && (
                    <button onClick={() => markRead(i.id)} data-testid={`mark-read-${i.id}`} title="Mark as read"
                      className="inline-flex items-center gap-1 text-xs text-ivory/60 hover:text-gold transition-colors border border-white/10 px-3 py-1.5">
                      <Check size={14} /> Mark read
                    </button>
                  )}
                  <button onClick={() => remove(i.id)} data-testid={`delete-${i.id}`} title="Delete"
                    className="text-ivory/40 hover:text-red-400 transition-colors"><Trash2 size={16} /></button>
                </div>
              </div>

              <p className="mt-4 text-ivory/70 leading-relaxed">{i.message}</p>

              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 text-sm text-ivory/50">
                <a href={`mailto:${i.email}`} className="inline-flex items-center gap-2 hover:text-gold"><Mail size={14} /> {i.email}</a>
                {i.phone && <span className="inline-flex items-center gap-2"><Phone size={14} /> {i.phone}</span>}
                {i.company && <span className="inline-flex items-center gap-2"><Building2 size={14} /> {i.company}</span>}
                <span className="text-ivory/30">{new Date(i.created_at).toLocaleString()}</span>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}
